import React from "react";
import PropTypes from "prop-types";
import Q360EmptyState from "../../UI/States/Q360EmptyState";

export function resolveStudioElementSpatial(element = null) {
  if (!element || typeof element !== "object") {
    return {};
  }

  const spatial =
    element.spatial && typeof element.spatial === "object"
      ? element.spatial
      : {};

  return {
    x: spatial.x ?? element.x,
    y: spatial.y ?? element.y,
    width: spatial.width ?? element.width,
    height: spatial.height ?? element.height,
    rotation: spatial.rotation ?? element.rotation,
    zIndex: spatial.zIndex ?? element.zIndex,
  };
}

export default function StudioTransformPanel({
  element,
  label = "Élément",
  type = "Élément",
  nudgeStep = 32,
  onMove = null,
}) {
  if (!element) {
    return (
      <Q360EmptyState
        message="Sélectionne un élément pour afficher ses paramètres."
        compact
        className="studio-framework-inspector__empty"
      />
    );
  }

  const spatial = resolveStudioElementSpatial(element);
  const canMove = typeof onMove === "function";

  return (
    <section className="studio-framework-inspector__panel">
      <p className="studio-framework-inspector__eyebrow">
        Élément sélectionné
      </p>

      <h4 className="studio-framework-inspector__selection-label">
        {label}
      </h4>

      <dl className="studio-framework-inspector__properties">
        {(element.uuid || element.id) && (
          <div>
            <dt>Identifiant</dt>
            <dd>{element.uuid || element.id}</dd>
          </div>
        )}

        <div>
          <dt>Type</dt>
          <dd>{type}</dd>
        </div>

        <div>
          <dt>X</dt>
          <dd>{spatial.x ?? "—"}</dd>
        </div>

        <div>
          <dt>Y</dt>
          <dd>{spatial.y ?? "—"}</dd>
        </div>

        {spatial.width != null && (
          <div>
            <dt>Largeur</dt>
            <dd>{spatial.width}</dd>
          </div>
        )}

        {spatial.height != null && (
          <div>
            <dt>Hauteur</dt>
            <dd>{spatial.height}</dd>
          </div>
        )}

        {spatial.rotation != null && (
          <div>
            <dt>Rotation</dt>
            <dd>{spatial.rotation}°</dd>
          </div>
        )}
      </dl>

      <div
        className="studio-framework-inspector__nudge-grid"
        aria-label="Déplacer la sélection"
      >
        <button
          type="button"
          aria-label="Déplacer vers le haut"
          disabled={!canMove}
          onClick={() => onMove?.({ x: 0, y: -nudgeStep })}
        >
          ↑
        </button>

        <button
          type="button"
          aria-label="Déplacer vers la gauche"
          disabled={!canMove}
          onClick={() => onMove?.({ x: -nudgeStep, y: 0 })}
        >
          ←
        </button>

        <button
          type="button"
          aria-label="Déplacer vers la droite"
          disabled={!canMove}
          onClick={() => onMove?.({ x: nudgeStep, y: 0 })}
        >
          →
        </button>

        <button
          type="button"
          aria-label="Déplacer vers le bas"
          disabled={!canMove}
          onClick={() => onMove?.({ x: 0, y: nudgeStep })}
        >
          ↓
        </button>
      </div>
    </section>
  );
}

const spatialPropType = PropTypes.shape({
  x: PropTypes.number,
  y: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  rotation: PropTypes.number,
  zIndex: PropTypes.number,
});

StudioTransformPanel.propTypes = {
  element: PropTypes.shape({
    uuid: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    rotation: PropTypes.number,
    zIndex: PropTypes.number,
    spatial: spatialPropType,
  }),
  label: PropTypes.node,
  type: PropTypes.node,
  nudgeStep: PropTypes.number,
  onMove: PropTypes.func,
};
